import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { localize, Message } from '@oneflowab/pomes';
import * as RadixToast from '@radix-ui/react-toast';
import type { MessageTranslator } from '@oneflowab/pomes';

import ErrorBoundary from 'components/error-boundary/error-boundary';
import NewCrossIcon from 'components/icons/new-cross';
import ToastIcon from 'components/toasts/toast-icon';
import type { Height, Toast } from 'components/toasts/types';

import { GAP, TOAST_TYPES, TIME_BEFORE_UNMOUNT, TOAST_WIDTH } from './constants';
import style from './toast.module.scss';
import contentStyle from './content/content.module.scss';

type Props = {
  id: Toast['id'];
  removeToast: (id: Toast['id']) => void;
  children: React.ReactNode;
  setHeights: React.Dispatch<React.SetStateAction<Height[]>>;
  message: MessageTranslator;
};

const ToastItemErrorBoundaryImpl = ({ children, id, removeToast, setHeights, message }: Props) => {
  const [mounted, setMounted] = useState(false);
  const [offsetBeforeRemove, setOffsetBeforeRemove] = useState(0);
  const toastRef = useRef<HTMLLIElement>(null);
  const offset = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: The callback needs to be memoized
  const handleClose = useCallback(() => {
    setMounted(false);

    setOffsetBeforeRemove(offset.current);
    setHeights((_heights) => _heights.filter((height) => height.toastId !== id));

    setTimeout(() => {
      removeToast(id);
    }, TIME_BEFORE_UNMOUNT);
  }, [id, removeToast]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: The effect needs to be memoized
  useEffect(() => {
    const toastNode = toastRef.current;

    if (toastNode) {
      const { height } = toastNode.getBoundingClientRect();

      // Add toast height tot heights array after the toast is mounted
      setHeights((_heights) => [{ toastId: id, height }, ..._heights]);

      return () => setHeights((_heights) => _heights.filter((_height) => _height.toastId !== id));
    }
  }, [id]);

  useEffect(() => {
    // For us to have the toast animation when it's mounted
    // If we set the initial state to true, the toast will not be animated
    setMounted(true);
  }, []);

  return (
    <ErrorBoundary
      customError={
        <RadixToast.Root
          ref={toastRef}
          className={style.ToastItem}
          style={
            {
              '--gap': `${GAP}px`,
              '--width': `${TOAST_WIDTH}px`,
              '--offset': `${!mounted ? offsetBeforeRemove : offset.current}px`,
              userSelect: 'auto',
            } as React.CSSProperties
          }
          data-mounted={mounted}
        >
          <div className={clsx(contentStyle.Container, contentStyle.ErrorToast)}>
            <div className={contentStyle.Left}>
              <ToastIcon type={TOAST_TYPES.ERROR} />
            </div>
            <div className={contentStyle.Middle}>
              <div className={contentStyle.Content}>
                <div className={contentStyle.Title}>
                  <Message
                    id="Something went wrong!"
                    comment="Title displayed when a toast notification fails to load"
                  />
                </div>
                <div className={contentStyle.Description}>
                  <Message
                    id="Please contact support."
                    comment="Error description for failed toast notification; advises contacting support"
                  />
                </div>
              </div>
            </div>
            <button
              aria-label={
                message({
                  id: 'Close error notification',
                  comment: 'Aria label for the close button of an error notification',
                }) as string
              }
              className={contentStyle.CloseButton}
              onClick={handleClose}
            >
              <NewCrossIcon width="13px" />
            </button>
          </div>
        </RadixToast.Root>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

export const ToastItemErrorBoundary = localize(ToastItemErrorBoundaryImpl) as unknown as React.FC<
  Omit<Props, 'message'>
>;
